import { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  useGetFiltersReactionsQuery,
  useGetReactionsMutation,
} from "@/app/kpi/kpi.api";
import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import DescriptionHeadings from "@/shared/components/atoms/DescriptionHeadings";
import { t } from "i18next";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import {
  optionAvatar,
  SelectAvatar,
} from "@/shared/components/organisms/SelectAvatar";
import { Option } from "@/shared/components/molecules/OptionsSelect";
import { MultiSelect } from "@/shared/components/organisms/MultiSelect";
import { initializeAmChart } from "@/shared/utils/chartUtils";

const ServiceComponent = () => {
  const { data: getFilterReactions } = useGetFiltersReactionsQuery({});
  const [optionsUsers, setOptionsUsers] = useState<optionAvatar[]>([]);
  const [optionsLessons, setOptionsLessons] = useState<Option[]>([]);
  const [optionsAssisted, setOptionsAssisted] = useState<Option[]>([]);
  const [responseKpi, setResponseKpi] = useState<any>({});

  useEffect(() => {
    if (getFilterReactions) {
      const users = getFilterReactions["users"].map(
        ({ id, name, avatar }: any) => ({
          id,
          name,
          avatar,
        })
      );

      const lessons = getFilterReactions["lessons"].map(
        ({ id, name }: any) => ({
          id,
          name,
        })
      );

      const assisteds = getFilterReactions["assisteds"].map(
        ({ id, name }: any) => ({
          id,
          name,
        })
      );

      setOptionsUsers(users);
      setOptionsLessons(lessons);
      setOptionsAssisted(assisteds);
    }
  }, [getFilterReactions]);

  function serviceRatingChart1(responseData: any) {
    am5.ready(function () {
      const root = initializeAmChart("monthEmotion");

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          layout: root.verticalLayout,
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          pinchZoomX: true,
        })
      );

      if (root._logo) {
        root._logo.dispose();
      }

      const emptyLabel = root.container.children.push(
        am5.Label.new(root, {
          text: t("share.views.no_data"),
          fontSize: 34,
          fill: am5.color("#cbcac8"),
          centerX: am5.p50,
          centerY: am5.p50,
          x: am5.p50,
          y: am5.p50,
          horizontalCenter: "middle",
          verticalCenter: "middle",
        })
      );

      // Disable zoom
      chart.set("panX", false);
      chart.set("panY", false);
      chart.set("wheelX", "none");
      chart.set("wheelY", "none");
      chart.set("pinchZoomX", false);
      chart.set("pinchZoomY", false);

      const xRenderer = am5xy.AxisRendererX.new(root, {
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        minorGridEnabled: true,
        minGridDistance: 10,
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: "month",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );

      xRenderer.grid.template.setAll({
        location: 1,
        visible: false,
      });

      // Create Y-Axis
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {}),
        })
      );

      // Store references to series for updating later
      const seriesMap = {};

      // Function to initialize series
      responseData.forEach(function (reaction: any) {
        const series: any = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: reaction.name,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: reaction.name,
            categoryXField: "month",
          })
        );
        seriesMap[reaction.name] = series;
      });

      emptyLabel.set("visible", true);
      chart.set("visible", false);

      chart.appear(1000, 100);
    });
  }

  type FormikProps = {
    users: number[];
    lessons: number[][];
    assisted: number[][];
  };

  const formik = useFormik<FormikProps>({
    initialValues: {
      users: [],
      lessons: [],
      assisted: [],
    },
    onSubmit: async (values) => {
      console.log("submit");
      try {
        const response = await getReactionsMutation(values);
        setResponseKpi(response);
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
  });

  const [getReactionsMutation] = useGetReactionsMutation();

  useEffect(() => {
    if (responseKpi.data) {
      serviceRatingChart1(responseKpi.data);
    } else {
      serviceRatingChart1([]);
    }
  }, [responseKpi]);
  return (
    <>
      <div className="p-4">
        <DescriptionHeadings
          title={t("user.views.graphic_reactions_h1")}
          description={t("user.views.graphic_reactions_p")}
        />
      </div>
      <div className="grid grid-cols-12 gap-4 p-4">
        <div className="bg-white shadow rounded-lg p-4 col-span-12 sm:col-span-12">
          <div className="mb-4">
            <h4 className="text-base font-semibold leading-6 text-gray-900 text-center">
              {t("user.views.graphic_reactions_title")}
            </h4>
          </div>
          <div className="chart">
            <div
              id="monthEmotion"
              style={{ width: "100%", height: "400px" }}
            ></div>
          </div>
          <div className="body">
            <form onSubmit={formik.handleSubmit}>
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b text-base text-gray-400">
                      #
                    </th>
                    <th className="py-2 px-4 border-b text-base text-gray-400">
                      {t("posts-master.views.specialist")}
                    </th>
                    <th className="py-2 px-4 border-b text-base text-gray-400">
                      {t("posts-master.views.event_types.lessons")}
                    </th>
                    <th className="py-2 px-4 border-b text-base text-gray-400">
                      {t("posts-master.views.event_types.assisteds")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[0, 1, 2, 3].map((index) => (
                    <tr key={index}>
                      <td className="py-2 px-4 border-b text-base text-gray-400 text-center">
                        {index + 1}
                      </td>
                      <td className="py-2 px-4 border-b">
                        <SelectAvatar
                          options={optionsUsers}
                          selected={optionsUsers.find(
                            (user) => user.id === formik.values.users[index]
                          )}
                          setSelected={(value) =>
                            formik.setFieldValue(`users[${index}]`, value.id)
                          }
                          tittle=""
                        />
                      </td>
                      <td className="py-2 px-4 border-b">
                        <MultiSelect
                          title=""
                          options={optionsLessons}
                          selectedOptions={optionsLessons.filter(
                            (lesson: any) =>
                              (formik.values.lessons[index] || []).includes(
                                lesson.id
                              )
                          )}
                          setSelectedOptions={(selectedOptions: Option[]) => {
                            const selectedIds = selectedOptions.map(
                              (option: any) => option.id
                            );
                            formik.setFieldValue(
                              `lessons[${index}]`,
                              selectedIds
                            );
                          }}
                        />
                      </td>
                      <td className="py-2 px-4 border-b">
                        <MultiSelect
                          title=""
                          options={optionsAssisted}
                          selectedOptions={optionsAssisted.filter(
                            (assisted: any) =>
                              (formik.values.assisted[index] || []).includes(
                                assisted.id
                              )
                          )}
                          setSelectedOptions={(selectedOptions: Option[]) => {
                            const selectedIds = selectedOptions.map(
                              (option: any) => option.id
                            );
                            formik.setFieldValue(
                              `assisted[${index}]`,
                              selectedIds
                            );
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-4 flex justify-end mr-4">
                <ButtonPrimary
                  size="medium"
                  text={t("user.views.graphic_reactions_button")}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceComponent;
